import { Injectable, inject } from '@angular/core';
import { Token } from './models/token';
import { User } from '../models/user.model';
import { Observable, map } from 'rxjs';
import { Organization } from '../models/organization.model';
import { AuthOptions, OidcSecurityService } from 'angular-auth-oidc-client';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly oidcService = inject(OidcSecurityService);

  isAuthenticated$: Observable<boolean> = this.oidcService.isAuthenticated$.pipe(
    map(result => result.isAuthenticated)
  );

  isAuthenticated(): boolean {
    let authenticated = false;
    this.oidcService.isAuthenticated$.subscribe(result => {
      authenticated = result.isAuthenticated;
    }).unsubscribe();
    return authenticated;
  }

  getToken(): Token | null {
    let token: Token | null = null;
    this.oidcService.getPayloadFromAccessToken().subscribe(payload => {
      token = payload as Token;
    }).unsubscribe();
    return token;
  }

  getUserFromToken(): Observable<User | null> {
    return this.oidcService.getPayloadFromAccessToken().pipe(
      map(payload => {
        if (!payload) return null;
        const token = payload as Token;
        return this.buildUser(token);
      })
    );
  }

  getOrganizationFromToken(): Observable<Organization | null> {
    return this.oidcService.getPayloadFromAccessToken().pipe(
      map(payload => {
        if (!payload) return null;
        const token = payload as Token;
        return this.getOrganizationInfo(token);
      })
    );
  }

  switchOrganization(organizationId: string): void {
    const authOptions: AuthOptions = {
      customParams: {
        tenant_id: organizationId
      }
    }

    const configurationId = undefined;

    this.oidcService.authorize(configurationId, authOptions);
  }

  logout = () => {
    this.oidcService.logoff().subscribe();
  }

  login(): void {
    const rememberedOrgId = this.getRememberedOrganizationId();
    if (rememberedOrgId) {
      this.switchOrganization(rememberedOrgId);
      return;
    }

    this.oidcService.authorize();
  }

  getRememberedOrganizationId(): string | null {
    try {
      const raw = localStorage.getItem('organization');
      if (!raw) return null;
      const stored = JSON.parse(raw) as Organization;
      return stored?.id ?? null;
    } catch {
      return null;
    }
  }

  private buildUser(token: Token): User {
    return {
      firstName: token.given_name,
      lastName: token.family_name,
      email: token.email,
    };
  }

  private getOrganizationInfo(token: Token): Organization | null {
    if (!token.organization) {
      return null;
    }

    return token.organization;
  }
}
